import { useTranslation } from "react-i18next";
import { User } from "lucide-react";

import { Weather } from "./Weather";
import { Greeting } from "./Greeting";
import { LocalTime } from "./LocalTime";
import { StatusBadge } from "./StatusBadge";
import { BannerIdentitySkeleton } from "./BannerIdentitySkeleton";
import { getPresenceDotColor } from "./presenceDotColor";
import { selectUser } from "@/store/slices/user.slice";
import { useAppSelector } from "@/hooks/useReduxHooks";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { BannerAlerts } from "./banner-alerts";
import { formatPerson } from "@/utils/helper";
import { cn } from "@/utils/cn";

export const DashboardBanner = () => {
  const { t } = useTranslation("my-space");
  const { user } = useAppSelector(selectUser);
  const displayName = formatPerson(user);
  const designation = user?.employmentDetails?.designation;

  /*
   * Three layouts, not two:
   *   mobile  — one column: identity, alerts, then the ambient time/weather rail.
   *   tablet  — identity and rail share the top row; alerts span the full width beneath.
   *   desktop — a single row: identity | alerts | rail.
   * The tablet arrangement comes from `order` on the alerts slot rather than duplicated
   * markup, so the DOM order stays identity → alerts → rail — which is also the correct
   * reading order on mobile, actionable before ambient.
   *
   * `shrink-0` on the section is load-bearing: the dashboard page is `h-full flex flex-col`,
   * so without it this is a shrinkable flex child — it gets squashed below its content
   * height and the hero's `overflow:hidden` then silently clips whatever spills, which is
   * what was cutting the alert pill off rather than growing the banner.
   */
  return (
    <section
      aria-label={t("labels.your_dashboard_summary")}
      className="bg-dashboard-hero relative grid min-h-44 w-full shrink-0 grid-cols-1 gap-x-4 gap-y-3 rounded-2xl p-3.5 shadow-sm ring-1 ring-primary/10 sm:grid-cols-[minmax(0,1fr)_auto] sm:p-4 sm:px-6 lg:min-h-40 lg:grid-cols-[minmax(0,auto)_minmax(0,1fr)_auto] lg:items-center"
    >
      {/* BG Pattern */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 z-0 -rotate-6 scale-[6] bg-[url('/images/mai-bg-pattern.svg')] bg-contain bg-center bg-repeat opacity-10"
      />

      {/* Identity */}
      {user ? (
        <div className="relative z-10 flex min-w-0 items-center gap-3 sm:min-w-44 sm:gap-3.5">
          <div className="relative shrink-0">
            <span
              aria-hidden
              className={cn(
                "absolute top-0 end-[-2px] z-10 size-3 rounded-full border-2 border-white",
                getPresenceDotColor(user.customStatus),
              )}
            />
            <Avatar className="flex size-14 items-center justify-center rounded-2xl border-2 border-white bg-white/60 shadow-sm sm:size-16 lg:size-20">
              <AvatarImage src={user.profileImage ?? undefined} alt={t("interp.profile_photo_alt", { name: displayName })} />
              <AvatarFallback className="rounded-2xl bg-white/60">
                <User size={24} className="text-primary" aria-hidden />
              </AvatarFallback>
            </Avatar>
            <div className="absolute inset-x-0 -bottom-2 flex justify-center">
              <StatusBadge />
            </div>
          </div>

          <div className="flex min-w-0 flex-col">
            <Greeting />
            <p className="truncate text-base font-semibold text-primary-strong uppercase sm:text-lg lg:text-xl">
              {displayName}
            </p>
            {designation && (
              <p className="truncate text-[11px] font-semibold text-primary sm:text-xs lg:text-sm">
                {designation}
              </p>
            )}
          </div>
        </div>
      ) : (
        <BannerIdentitySkeleton />
      )}

      {/* Alerts slot — ranked and capped by BannerAlerts, which renders nothing when
          there is nothing to act on. Collapsing beats filling it with a "you're all
          caught up" pill: every contextual candidate is already a widget below. */}
      <div className="relative z-10 flex min-w-0 items-center justify-center sm:order-last sm:col-span-2 lg:order-none lg:col-span-1">
        <BannerAlerts className="w-full lg:max-w-md" />
      </div>

      {/* Time + weather rail. Full-width on mobile, so the two halves sit at opposite
          edges instead of leaving the row half empty; a compact group from tablet up. */}
      <div className="relative z-10 flex min-w-0 items-center justify-between gap-3 sm:justify-end sm:gap-4">
        <LocalTime />
        <span aria-hidden className="hidden h-10 w-px bg-primary/15 sm:block" />
        <Weather />
      </div>
    </section>
  );
};
